/**
 * Live Total Price (qty × unit price) renderer for `snippets/quantity-selector.liquid`
 * Updates on:
 * - typing in qty input
 * - plus/minus clicks (ThemeEvents.quantitySelectorUpdate)
 * - variant changes (ThemeEvents.variantUpdate morphing)
 * - cart changes (ThemeEvents.cartUpdate morphing)
 */

/**
 * Format a cents value as fixed GBP (e.g. £1,234.56).
 * @param {number} cents
 * @returns {string}
 */
function formatGbp(cents) {
  const value = (cents / 100).toFixed(2); // "1234.56"
  const parts = value.split('.');
  const wholeRaw = parts[0] || '0';
  const fraction = parts[1] || '00';
  const whole = wholeRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `£${whole}.${fraction}`;
}

/**
 * Format area number nicely (e.g. 12.5 or 12.34).
 * @param {number} area
 * @returns {string}
 */
function formatArea(area) {
  if (!Number.isFinite(area)) return '0';
  const safe = Math.max(0, area);
  return safe.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * @param {HTMLElement} wrapper
 */
function updateTotalPriceForWrapper(wrapper) {
  /** @type {HTMLElement | null} */
  let totalEl = wrapper.querySelector('.total_price');

  // Fallback: allow .total_price to live anywhere inside the same form
  if (!totalEl) {
    const form = wrapper.closest('form');
    if (form) {
      totalEl = form.querySelector('.total_price');
    }
  }

  if (!totalEl) return;

  const input = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('input[ref="quantityInput"]'));
  if (!input) return;

  const qty = Number.parseInt(input.value || '0', 10);
  const unitPriceCents = Number.parseInt(totalEl.dataset.unitPrice || '0', 10);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPriceCents)) return;

  const totalCents = Math.max(0, qty) * Math.max(0, unitPriceCents);
  const formatted = formatGbp(totalCents);

  totalEl.textContent = `Total: ${formatted}`;
}

/**
 * @param {ParentNode} root
 */
function updateAllTotals(root = document) {
  const wrappers = root.querySelectorAll?.('.quantity-selector-wrapper') || [];
  wrappers.forEach((wrapper) => {
    if (wrapper instanceof HTMLElement) updateTotalPriceForWrapper(wrapper);
  });
}

/**
 * @param {HTMLElement} perBoxEl
 */
function updatePerBoxM2AreaForElement(perBoxEl) {
  const scope = perBoxEl.closest('form') || perBoxEl;
  const qtyInput = /** @type {HTMLInputElement | null} */ (
    scope.querySelector('input[name="quantity"], input[ref="quantityInput"]')
  );
  if (!qtyInput) return;

  const qty = Number.parseInt(qtyInput.value || '0', 10);
  const perBox = Number.parseFloat(perBoxEl.dataset.perBoxM2Area || '0');
  if (!Number.isFinite(qty) || !Number.isFinite(perBox)) return;

  const total = Math.max(0, qty) * Math.max(0, perBox);
  const formatted = formatArea(total);

  const totalSpan = /** @type {HTMLElement | null} */ (perBoxEl.querySelector('[data-per-box-m2-area-total]'));
  if (totalSpan) totalSpan.textContent = formatted;
}

/**
 * @param {ParentNode} root
 */
function updateAllPerBoxM2Areas(root = document) {
  const els = root.querySelectorAll?.('.per_box_area[data-per-box-m2-area]') || [];
  els.forEach((el) => {
    if (el instanceof HTMLElement) updatePerBoxM2AreaForElement(el);
  });
}

// Initial render
if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      updateAllTotals(document);
      updateAllPerBoxM2Areas(document);
    },
    { once: true }
  );
} else {
  updateAllTotals(document);
  updateAllPerBoxM2Areas(document);
}

// Live typing updates
document.addEventListener(
  'input',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('input[ref="quantityInput"]')) return;
    const wrapper = target.closest('.quantity-selector-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    updateTotalPriceForWrapper(wrapper);
    const scope = wrapper.closest('form') || document;
    updateAllPerBoxM2Areas(scope); 
  },
  { capture: true }
);

// Plus/minus + blur updates from the theme's quantity selector component
document.addEventListener('quantity-selector:update', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const wrapper = target.closest('.quantity-selector-wrapper');
  if (!(wrapper instanceof HTMLElement)) return;
  updateTotalPriceForWrapper(wrapper);
  const scope = wrapper.closest('form') || document;
  updateAllPerBoxM2Areas(scope);
});

// Variant/cart updates morph DOM, so re-scan afterwards
for (const eventName of ['variant:update', 'cart:update']) {
  document.addEventListener(eventName, (event) => {
    const scope = event.target instanceof HTMLElement ? event.target : document;
    setTimeout(() => {
      updateAllTotals(scope);
      updateAllPerBoxM2Areas(scope);
    }, 0);
  });
}

